import { useEffect, useMemo, useRef, useState } from "react";
import { Buildings, X, CheckCircle, CaretDown, MagnifyingGlass, Check } from "@phosphor-icons/react";
import { useVendorFilter } from "@/contexts/VendorFilterContext";

/**
 * VendorChipBar — reusable filter row for Jobs Board / Radar / WOs list.
 *
 * Compact layout: shows the top ~5 vendors as inline chips + an "All"
 * chip + a "+N more ▾" button that opens a searchable checkbox sheet for
 * the long tail. Selection persists via VendorFilterContext.
 */
export default function VendorChipBar({ dense = false, className = "", topN = 5 }) {
  const { selected, vendors, loading, toggle, clear, reload } = useVendorFilter();
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => { reload(auth()); /* eslint-disable-next-line */ }, []);

  if (loading && vendors.length === 0) {
    return (
      <div className={`text-[10px] font-black uppercase tracking-widest text-slate-400 ${className}`}>
        Loading vendors…
      </div>
    );
  }
  if (!vendors.length) return null;

  const active = selected.length > 0;

  // Split into two piles: primary (top-N by open count, excluding untagged) + rest.
  // Always surface any SELECTED vendor in the primary strip so it's visible.
  const untagged = vendors.find(v => v.slug === "__untagged__") || null;
  const namedVendors = vendors.filter(v => v.slug !== "__untagged__");
  // Sorted by open desc — mongo pipeline usually returns this already, but be safe.
  const sorted = [...namedVendors].sort((a, b) => (b.open || 0) - (a.open || 0));

  const primary = [];
  const rest = [];
  for (const v of sorted) {
    if (primary.length < topN || selected.includes(v.slug)) primary.push(v);
    else rest.push(v);
  }
  // Selected-but-in-rest? Bubble them up too so users see them.
  const stillHiddenSelected = rest.filter(v => selected.includes(v.slug));
  if (stillHiddenSelected.length) {
    for (const v of stillHiddenSelected) {
      primary.push(v);
      const idx = rest.indexOf(v);
      if (idx >= 0) rest.splice(idx, 1);
    }
  }

  const hiddenSelectedCount = 0; // by construction above, all selected are in primary
  const moreCount = rest.length;

  return (
    <div className={`${className}`} data-testid="vendor-chip-bar">
      <div className="flex items-center gap-1.5 flex-wrap">
        <div className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-slate-600 mr-1">
          <Buildings weight="fill" size={12} className="text-slate-500"/>
          Vendor
          {active && (
            <button onClick={clear} data-testid="vendor-clear-btn"
              className="ml-1 text-cyan-700 hover:text-rose-600 inline-flex items-center gap-0.5">
              <X weight="bold" size={10}/> Clear
            </button>
          )}
        </div>
        {/* "All" pill — highlighted when nothing is selected */}
        <button
          onClick={clear}
          data-testid="vendor-chip-all"
          className={`h-7 px-2 border-2 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 transition ${
            !active
              ? "bg-slate-900 border-slate-900 text-white"
              : "bg-white border-slate-300 text-slate-700 hover:border-slate-500"
          }`}>
          All
        </button>
        {/* Untagged sits right next to All for consistent thumb reach */}
        {untagged && <VendorChip v={untagged} on={selected.includes(untagged.slug)} onTap={() => toggle(untagged.slug)} dense={dense}/>}
        {/* Top-N by open count */}
        {primary.map(v => (
          <VendorChip key={v.slug} v={v} on={selected.includes(v.slug)} onTap={() => toggle(v.slug)} dense={dense}/>
        ))}
        {/* Overflow button */}
        {moreCount > 0 && (
          <button
            data-testid="vendor-more-btn"
            onClick={() => setSheetOpen(true)}
            className="h-7 px-2 border-2 border-dashed border-slate-400 bg-white hover:bg-slate-50 text-slate-600 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 rounded-none">
            +{moreCount} more
            <CaretDown weight="bold" size={10}/>
          </button>
        )}
        <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 ml-1 hidden sm:inline">
          Tap to add · tap again to remove
        </span>
      </div>
      {sheetOpen && (
        <VendorPickerSheet
          vendors={sorted}
          selected={selected}
          onToggle={toggle}
          onClose={() => setSheetOpen(false)}
          onClear={clear}
        />
      )}
    </div>
  );
}

function VendorChip({ v, on, onTap, dense }) {
  const isUntagged = v.slug === "__untagged__";
  return (
    <button
      onClick={onTap}
      data-testid={`vendor-chip-${v.slug}`}
      title={`${v.open} open · ${v.count} total`}
      className={`h-7 pl-2 pr-1.5 border-2 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1.5 transition ${
        on
          ? isUntagged
            ? "bg-slate-600 border-slate-700 text-white"
            : "bg-cyan-600 border-cyan-700 text-white"
          : isUntagged
            ? "bg-white border-slate-300 text-slate-500 hover:border-slate-500"
            : "bg-white border-slate-300 text-slate-700 hover:border-cyan-500"
      }`}>
      {on && <CheckCircle weight="fill" size={10}/>}
      <span className={dense ? "max-w-[10ch] truncate" : ""}>
        {v.name}
      </span>
      <span className={`ml-0.5 h-4 min-w-[16px] px-1 text-[9px] font-black tabular-nums inline-flex items-center justify-center ${
        on ? "bg-white/25" : isUntagged ? "bg-slate-100 text-slate-500" : "bg-slate-100 text-slate-600"
      }`}>
        {v.open}
      </span>
    </button>
  );
}

function VendorPickerSheet({ vendors, selected, onToggle, onClose, onClear }) {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return vendors;
    return vendors.filter(v => (v.name || "").toLowerCase().includes(q));
  }, [vendors, query]);

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4"
         data-testid="vendor-picker-sheet"
         onClick={onClose}>
      <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[85vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-4 border-b-2 border-slate-900 bg-slate-50 flex items-center gap-2">
          <Buildings weight="fill" size={20} className="text-slate-700"/>
          <div className="font-display font-black text-base uppercase tracking-tight flex-1">
            Pick Vendors · {selected.filter(s => s !== "__untagged__").length} selected
          </div>
          <button onClick={onClose} data-testid="vendor-picker-close"
                  className="h-9 w-9 flex items-center justify-center text-slate-500 hover:text-slate-800">
            <X size={20}/>
          </button>
        </div>
        {/* Search */}
        <div className="p-3 border-b border-slate-200">
          <div className="relative">
            <MagnifyingGlass weight="bold" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search vendors…"
              data-testid="vendor-picker-search"
              className="w-full h-10 pl-9 pr-3 border-2 border-slate-300 rounded-md text-sm font-medium focus:border-cyan-500 focus:outline-none"
            />
          </div>
        </div>
        {/* Checkbox list */}
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {filtered.length === 0 && (
            <div className="p-8 text-center text-sm font-black uppercase tracking-widest text-slate-400">
              No matches
            </div>
          )}
          {filtered.map(v => {
            const on = selected.includes(v.slug);
            return (
              <button
                key={v.slug}
                onClick={() => onToggle(v.slug)}
                data-testid={`vendor-picker-row-${v.slug}`}
                className={`w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-slate-50 ${on ? "bg-cyan-50" : ""}`}>
                <div className={`h-6 w-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                  on ? "bg-cyan-500 border-cyan-700" : "bg-white border-slate-300"
                }`}>
                  {on && <Check weight="bold" size={14} className="text-white"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-black truncate ${on ? "text-cyan-900" : "text-slate-800"}`}>
                    {v.name}
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    {v.open} open · {v.count} total
                  </div>
                </div>
                {on && <CheckCircle weight="fill" size={16} className="text-cyan-600 flex-shrink-0"/>}
              </button>
            );
          })}
        </div>
        {/* Footer */}
        <div className="p-3 border-t-2 border-slate-200 bg-slate-50 flex gap-2">
          {selected.filter(s => s !== "__untagged__").length > 0 && (
            <button onClick={onClear} data-testid="vendor-picker-clear"
                    className="h-10 px-4 border-2 border-red-400 bg-white text-red-600 font-black uppercase tracking-widest text-xs rounded-md hover:bg-red-50">
              Clear
            </button>
          )}
          <button onClick={onClose} data-testid="vendor-picker-done"
                  className="flex-1 h-10 border-2 border-slate-900 bg-slate-900 hover:bg-slate-800 text-white font-black uppercase tracking-widest text-xs rounded-md shadow-[2px_2px_0px_#0f172a]">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
